import { CheckCircle2, AlertCircle, AlertTriangle, Plus, Edit2 } from 'lucide-react';
import type { CierreDiaPreview } from '@shared/types';

/**
 * Sprint 19 — Card de insumo para el flujo de cierre del día.
 *
 * Semáforo de discrepancia entre stock teórico (lo que el sistema calcula)
 * y la lectura física (lo que la dueña contó). Reduce 8 columnas de tabla a
 * una unidad visual con estado.
 */

type DetalleInsumo = CierreDiaPreview['detalleInsumos'][number];

export type EstadoCierre =
  | 'agotado'             // teórico ≤ 0
  | 'sin_capturar'        // físico no ingresado
  | 'cuadra'              // físico capturado y Δ < 5%
  | 'diferencia_menor'    // físico capturado y 5% ≤ Δ < 15%
  | 'discrepancia';       // físico capturado y Δ ≥ 15%

const TOLERANCIA_CUADRA = 0.05;       // 5%
const TOLERANCIA_DIFERENCIA = 0.15;   // 15%

export function calcularEstadoCierre(teorico: number, fisico: number | null): EstadoCierre {
  if (teorico <= 0) return 'agotado';
  if (fisico === null) return 'sin_capturar';
  if (teorico === 0) return fisico === 0 ? 'cuadra' : 'discrepancia';
  const deltaAbs = Math.abs(fisico - teorico);
  const deltaPct = deltaAbs / Math.max(teorico, 0.001);
  if (deltaPct < TOLERANCIA_CUADRA) return 'cuadra';
  if (deltaPct < TOLERANCIA_DIFERENCIA) return 'diferencia_menor';
  return 'discrepancia';
}

const STYLE: Record<EstadoCierre, { borde: string; fondo: string; chip: string; label: string }> = {
  agotado:           { borde: 'border-slate-300', fondo: 'bg-slate-50',    chip: 'bg-slate-500 text-white',    label: '⚫ AGOTADO' },
  sin_capturar:      { borde: 'border-slate-200', fondo: 'bg-white',       chip: 'bg-slate-200 text-slate-700', label: '⚪ SIN CAPTURAR' },
  cuadra:            { borde: 'border-emerald-300', fondo: 'bg-emerald-50', chip: 'bg-emerald-600 text-white', label: '🟢 CUADRA' },
  diferencia_menor:  { borde: 'border-amber-400', fondo: 'bg-amber-50',    chip: 'bg-amber-500 text-white',   label: '🟡 DIFERENCIA' },
  discrepancia:      { borde: 'border-red-400',   fondo: 'bg-red-50',      chip: 'bg-red-600 text-white',     label: '🔴 DISCREPANCIA' },
};

interface Props {
  detalle: DetalleInsumo;
  fisico: number | null;
  onCapturar: () => void;
}

export function InsumoCierreCard({ detalle, fisico, onCapturar }: Props) {
  const estado = calcularEstadoCierre(detalle.teorico, fisico);
  const style = STYLE[estado];
  const tieneCaptura = fisico !== null;
  const delta = tieneCaptura ? fisico! - detalle.teorico : 0;
  const deltaPct = detalle.teorico > 0 ? Math.abs(delta) / detalle.teorico * 100 : 0;

  return (
    <div className={`rounded-xl border-2 ${style.borde} ${style.fondo} p-3 flex flex-col gap-2 shadow-sm hover:shadow-md transition-shadow`}>
      <div className="flex items-center justify-between gap-2">
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wide ${style.chip}`}>
          {style.label}
        </span>
        <span className="text-xs uppercase tracking-wide text-slate-600">{detalle.unidad}</span>
      </div>

      <h4 className="text-base font-bold leading-tight text-slate-800">{detalle.nombre}</h4>

      {/* Métricas del día (compactas) */}
      <div className="grid grid-cols-3 gap-1 text-[11px]">
        <Mini label="+ Cargado" valor={detalle.cargado} color="text-emerald-700" />
        <Mini label="− Vendido" valor={detalle.vendido} color="text-blue-700" />
        <Mini label="− Merma"   valor={detalle.merma}   color="text-red-700" />
      </div>

      {/* Comparación teórico vs físico */}
      <div className="rounded-lg bg-white border border-slate-200 p-2 text-xs">
        <div className="flex justify-between">
          <span className="text-slate-500">Teórico:</span>
          <span className="font-mono font-semibold">{detalle.teorico.toFixed(2)} {detalle.unidad}</span>
        </div>
        {tieneCaptura ? (
          <>
            <div className="flex justify-between mt-0.5">
              <span className="text-slate-500">Físico:</span>
              <span className="font-mono font-semibold">{fisico!.toFixed(2)} {detalle.unidad}</span>
            </div>
            <div className={`flex justify-between mt-1 pt-1 border-t ${
              estado === 'cuadra' ? 'border-emerald-200 text-emerald-700' :
              estado === 'diferencia_menor' ? 'border-amber-200 text-amber-700' :
              estado === 'discrepancia' ? 'border-red-200 text-red-700' : 'border-slate-200'
            }`}>
              <span className="font-medium">Diferencia:</span>
              <span className="font-mono font-bold">
                {delta >= 0 ? '+' : ''}{delta.toFixed(2)} ({deltaPct.toFixed(0)}%)
              </span>
            </div>
          </>
        ) : (
          <div className="text-center text-[11px] italic text-slate-400 mt-1">
            Captura el físico para comparar
          </div>
        )}
      </div>

      {/* Acción */}
      <button
        onClick={onCapturar}
        className={`btn-ghost w-full justify-center text-xs ${tieneCaptura ? 'text-slate-700' : 'text-primary font-medium'}`}
        title={tieneCaptura ? 'Editar lectura física' : 'Capturar lectura física'}
      >
        {tieneCaptura ? <Edit2 className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
        {tieneCaptura ? 'Editar conteo' : 'Capturar conteo'}
      </button>

      {/* Anotación operativa según estado */}
      {estado === 'diferencia_menor' && (
        <div className="text-[11px] text-amber-800 flex items-start gap-1">
          <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
          <span>Se registrará un AJUSTE de {delta >= 0 ? '+' : ''}{delta.toFixed(2)} al cerrar.</span>
        </div>
      )}
      {estado === 'discrepancia' && (
        <div className="text-[11px] text-red-800 flex items-start gap-1">
          <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
          <span>Diferencia mayor al 15%. Revisa antes de cerrar.</span>
        </div>
      )}
      {estado === 'cuadra' && (
        <div className="text-[11px] text-emerald-800 flex items-start gap-1">
          <CheckCircle2 className="h-3 w-3 mt-0.5 shrink-0" />
          <span>Sin acción requerida.</span>
        </div>
      )}
    </div>
  );
}

function Mini({ label, valor, color }: { label: string; valor: number; color: string }) {
  return (
    <div className="text-center">
      <div className="text-xs uppercase tracking-wide text-slate-600">{label}</div>
      <div className={`font-mono ${color}`}>{valor > 0 ? valor.toFixed(2) : '—'}</div>
    </div>
  );
}
